/**
 * useSettingsState - Custom hook for Site Settings module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

export const useSettingsState = ({ setError, setSuccess, setLoading }) => {
  // Site settings state
  const [siteSettings, setSiteSettings] = useState(null);
  const [selectedSettingsTab, setSelectedSettingsTab] = useState('basic');

  // Fetch site settings
  const fetchSiteSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/cms/settings`);
      setSiteSettings(response.data);
    } catch (err) {
      setError('Failed to fetch site settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading]);

  // Update site settings
  const updateSiteSettings = useCallback(async (updatedSettings) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API_URL}/api/cms/settings`, updatedSettings);
      setSiteSettings(response.data);
      setSuccess('Site settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update site settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [setError, setSuccess, setLoading]);

  // Handle settings field change
  const handleSettingsChange = useCallback((field, value) => {
    setSiteSettings(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle nested settings change
  const handleNestedSettingsChange = useCallback((parent, child, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: value
      }
    }));
  }, []);

  // Save settings
  const handleSaveSettings = useCallback(() => {
    if (siteSettings) {
      updateSiteSettings(siteSettings);
    }
  }, [siteSettings, updateSiteSettings]);

  return {
    // State
    siteSettings,
    selectedSettingsTab,

    // Setters
    setSiteSettings,
    setSelectedSettingsTab,

    // Actions
    fetchSiteSettings,
    updateSiteSettings,
    handleSettingsChange,
    handleNestedSettingsChange,
    handleSaveSettings,
  };
};

export default useSettingsState;
